import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
    LayoutDashboard,
    Book,
    PieChart,
    Settings,
    LogOut,
    Menu,
    X,
    Home,
    TrendingUp,
    FileText,
    Bell,
    User
} from 'lucide-react';
import clsx from 'clsx';

const Sidebar = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();

    const navItems = [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
        { icon: Book, label: 'Books', path: '/ledgers' },
        { icon: PieChart, label: 'Reports', path: '/reports' },
        { icon: Settings, label: 'Settings', path: '/settings' },
    ];

    const bottomNavItems = [
        { icon: Home, label: 'Home', path: '/home' },
        { icon: TrendingUp, label: 'Stats', path: '/stats' },
        { icon: FileText, label: 'Add', path: '/add-expense' },
        { icon: Bell, label: 'Alerts', path: '/alerts' },
        { icon: User, label: 'Profile', path: '/profile' },
    ];

    // Close mobile menu when route changes
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location.pathname]);

    // Close menu on escape key press
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') setIsMobileMenuOpen(false);
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, []);

    // Prevent body scroll when mobile menu is open
    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isMobileMenuOpen]);

    return (
        <>
            {/* Desktop Sidebar */}
            <aside className="hidden lg:flex w-64 bg-white h-screen border-r border-gray-200 flex-col fixed left-0 top-0 z-40">
                <div className="p-5 flex items-center gap-3 border-b border-gray-100">
                    <img
                        src="/softye_expense_tracker.png"
                        alt="Softye Logo"
                        className="w-8 h-8 object-contain"
                    />
                    <span className="text-base font-bold text-gray-900">Softye Expense</span>
                </div>

                <nav className="flex-1 px-3 py-4 space-y-1">
                    {navItems.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            className={({ isActive }) =>
                                clsx(
                                    'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-sm',
                                    isActive
                                        ? 'bg-indigo-50 text-indigo-600 font-medium'
                                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                )
                            }
                        >
                            <item.icon size={18} className="transition-colors" />
                            <span>{item.label}</span>
                        </NavLink>
                    ))}
                </nav>

                <div className="p-3 border-t border-gray-100">
                    <button
                        onClick={() => {
                            localStorage.removeItem('token');
                            window.location.href = '/login';
                        }}
                        className="flex items-center gap-3 px-3 py-2.5 w-full text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 text-sm"
                    >
                        <LogOut size={18} />
                        <span>Logout</span>
                    </button>
                </div>
            </aside>

            {/* Mobile Header with Hamburger */}
            <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200">
                <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                            aria-label="Toggle menu"
                        >
                            {isMobileMenuOpen ? (
                                <X size={24} className="text-gray-700" />
                            ) : (
                                <Menu size={24} className="text-gray-700" />
                            )}
                        </button>
                        <div className="flex items-center gap-2">
                            <img
                                src="/softye_expense_tracker.png"
                                alt="Softye Logo"
                                className="w-8 h-8 object-contain"
                            />
                            <span className="text-sm font-bold text-gray-900">Softye</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <button className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                            <Bell size={20} className="text-gray-600" />
                        </button>
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                            <User size={18} className="text-gray-600" />
                        </div>
                    </div>
                </div>
            </header>

            {/* Mobile Sidebar Overlay */}
            {isMobileMenuOpen && (
                <div
                    className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
                    onClick={() => setIsMobileMenuOpen(false)}
                />
            )}

            {/* Mobile Sidebar Drawer */}
            <aside className={clsx(
                'lg:hidden fixed top-0 left-0 h-screen w-64 bg-white z-50 transform transition-transform duration-300 ease-in-out shadow-xl',
                isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
            )}>
                <div className="p-5 flex items-center justify-between border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <img
                            src="/softye_expense_tracker.png"
                            alt="Softye Logo"
                            className="w-8 h-8 object-contain"
                        />
                        <span className="text-base font-bold text-gray-900">Softye Expense</span>
                    </div>
                    <button
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                        aria-label="Close menu"
                    >
                        <X size={20} className="text-gray-600" />
                    </button>
                </div>

                <div className="h-[calc(100vh-140px)] overflow-y-auto">
                    <div className="p-4">
                        <div className="mb-6">
                            <div className="flex items-center gap-3 px-3 mb-4">
                                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                                    <User size={20} className="text-indigo-600" />
                                </div>
                                <div>
                                    <p className="font-medium text-gray-900">Rajesh Kumar</p>
                                    <p className="text-sm text-gray-500">rajesh@example.com</p>
                                </div>
                            </div>
                        </div>

                        <nav className="space-y-1">
                            {navItems.map((item) => (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    className={({ isActive }) =>
                                        clsx(
                                            'flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200',
                                            isActive
                                                ? 'bg-indigo-50 text-indigo-600 font-medium'
                                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                        )
                                    }
                                >
                                    <item.icon size={20} className="transition-colors" />
                                    <span className="text-sm">{item.label}</span>
                                </NavLink>
                            ))}
                        </nav>

                        <div className="mt-8 pt-6 border-t border-gray-100">
                            <div className="px-3 mb-4">
                                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Quick Actions</p>
                            </div>
                            <button className="flex items-center gap-3 px-3 py-3 w-full text-gray-600 hover:bg-green-50 hover:text-green-600 rounded-lg transition-all duration-200">
                                <FileText size={20} />
                                <span className="text-sm">Add New Expense</span>
                            </button>
                            <button className="flex items-center gap-3 px-3 py-3 w-full text-gray-600 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-all duration-200">
                                <TrendingUp size={20} />
                                <span className="text-sm">View Insights</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-100 bg-white">
                    <button
                        onClick={() => {
                            localStorage.removeItem('token');
                            window.location.href = '/login';
                        }}
                        className="flex items-center gap-3 px-3 py-3 w-full text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                    >
                        <LogOut size={20} />
                        <span className="text-sm">Logout</span>
                    </button>
                </div>
            </aside>

            {/* Mobile Bottom Navigation 
            <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40 shadow-lg">
                <div className="flex justify-around items-center px-2 py-2">
                    {bottomNavItems.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            className={({ isActive }) =>
                                clsx(
                                    'flex flex-col items-center p-2 rounded-xl transition-all duration-200 flex-1 mx-1',
                                    isActive
                                        ? 'text-indigo-600'
                                        : 'text-gray-500 hover:text-gray-900'
                                )
                            }
                        >
                            <div className={clsx(
                                'p-2 rounded-lg mb-1',
                                location.pathname === item.path ? 'bg-indigo-50' : ''
                            )}>
                                <item.icon size={20} />
                            </div>
                            <span className="text-xs font-medium">{item.label}</span>
                        </NavLink>
                    ))}
                </div>
            </nav>
*/}
            {/* Add padding for mobile bottom nav */}
            <div className="lg:hidden h-16" />
        </>
    );
};

export default Sidebar;